// # Library Header Component
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link, useLocation, useParams } from 'react-router-dom';
import { useAuthStore } from '../../../auth/state';
import { auth } from '../../../auth/firebase';
import { FaArrowLeft, FaSignOutAlt } from 'react-icons/fa';
import { MdLightMode, MdDarkMode } from 'react-icons/md';  // # Remove MdNoteAdd, MdFlashOn icons
import { FiHome, FiUsers, FiPackage, FiBook, FiSettings, FiAlertCircle, FiSearch } from 'react-icons/fi';  // # Import menu icons and search
import { BiCoffee, BiTestTube, BiNotepad, BiCard } from 'react-icons/bi';  // # Import quick links icons
import Loading from '../../../components/common/Loading';  // # Import Loading component
import LibrarySearch from '../../../search/lib_search';  // # Import search component
import { doc, updateDoc } from 'firebase/firestore';  // # Import for updating theme preference
import { db } from '../../../auth/firebase';  // # Import Firebase db
import cacheManager from '../../../auth/cache_manager';  // # Import cache manager
import loginOutManager from '../../../auth/login_out_manager';  // # Import login manager
import './lib_header.css';

const LibraryHeader = ({ onThemeToggle }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { resetAuth, isAdmin, user } = useAuthStore();
  const [isSigningOut, setIsSigningOut] = useState(false);  // # Add loading state for sign out
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isQuickLinksOpen, setIsQuickLinksOpen] = useState(false);
  const [isDarkMode, setIsDarkMode] = useState(false);  // # Track current theme
  const [isSignOutModalOpen, setIsSignOutModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);  // # Add search state
  
  // # Check if we're in iframe mode (chapter view) - only show theme toggle in this case
  const isChapterView = !!onThemeToggle;

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.lib_theme !== undefined) {
          setIsDarkMode(userData.themes.lib_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.lib_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              lib_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
      
      // # Call the onThemeToggle prop function if provided
      if (onThemeToggle) {
        onThemeToggle(newTheme);
      }
      
      // # Dispatch theme change event for footer and other components
      const themeChangeEvent = new CustomEvent('theme_change', {
        detail: { isDark: newTheme }
      });
      window.dispatchEvent(themeChangeEvent);
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Get current context from URL
  const getContext = () => {
    const path = location.pathname;
    const isStep1 = path.includes('step1');
    const isStep2 = path.includes('step2');
    const chapterId = params.chapterId;
    
    return {
      isStep1,
      isStep2,
      chapterId,
      stepId: isStep1 ? 'USMLE Step 1 -' : isStep2 ? 'USMLE Step 2 -' : null
    };
  };

  // # Quick Links data with context-aware paths and icons
  const getQuickLinks = () => {
    const { isStep1, isStep2, chapterId, stepId } = getContext();
    const basePath = isAdmin ? '/admin' : '';
    
    // # Helper function to format chapter name for bookmarks
    const formatChapterForBookmarks = (chapter) => {
      if (!chapter) return null;
      // # Remove any special characters and convert to lowercase
      return chapter.toLowerCase()
        .replace(/[\s&]+/g, '_')
        .replace(/[^a-z0-9_]/g, '');
    };

    // # Get bookmark path based on context
    const getBookmarkPath = () => {
      if (chapterId) {
        const formattedChapter = formatChapterForBookmarks(chapterId);
        if (isStep1) {
          return `${basePath}/bookmarks/step1/${formattedChapter}`;
        } else if (isStep2) {
          return `${basePath}/bookmarks/step2/${formattedChapter}`;
        }
      }
      // # Default paths if no chapter or step context
      if (isStep1) return `${basePath}/bookmarks/step1`;
      if (isStep2) return `${basePath}/bookmarks/step2`;
      return `${basePath}/bookmarks`;
    };
    
    return [
      {
        title: 'Home',
        path: isAdmin ? '/admin' : '/dashboard',
        icon: <FiHome className="libhdr_quick-link-icon" />
      },
      {
        title: 'Labs',
        path: `${basePath}/labs`,
        icon: <BiTestTube className="libhdr_quick-link-icon" />
      },
      {
        title: 'Take a Break',
        path: `${basePath}/community/break`,
        icon: <BiCoffee className="libhdr_quick-link-icon" />
      },
      {
        title: 'Notes',
        path: `${basePath}/notes`,
        icon: <BiNotepad className="libhdr_quick-link-icon" />
      },
      {
        title: 'Flashcards',
        path: `${basePath}/flashcards`,
        icon: <BiCard className="libhdr_quick-link-icon" />
      },
      {
        title: 'Bookmarks',
        path: getBookmarkPath(),
        icon: <FiBook className="libhdr_quick-link-icon" />
      }
    ];
  };

  // # Handle back navigation
  const handleBack = () => {
    navigate(-1); // Goes back one step in history
  };

  // # Handle logo click - redirect to appropriate dashboard
  const handleLogoClick = () => {
    navigate(isAdmin ? '/admin' : '/dashboard');
  };

  // # Handle menu toggle
  const toggleMenu = useCallback((e) => {
    if (e) e.stopPropagation();
    setIsMenuOpen(prev => !prev);
  }, []);

  // # Handle quick links toggle
  const toggleQuickLinks = useCallback((e) => {
    if (e) e.stopPropagation();
    setIsQuickLinksOpen(prev => !prev);
  }, []);

  // # Handle navigation
  const handleNavigation = useCallback(() => {
    setIsMenuOpen(false);
    setIsQuickLinksOpen(false);
  }, []);

  // # Handle sign out
  const handleSignOut = async () => {
    try {
      setIsSigningOut(true);  // # Start loading
      // # Record sign out activity before signing out
      if (auth.currentUser) {
        await loginOutManager.recordSignOutActivity(auth.currentUser.uid);
      }
      await auth.signOut();
      resetAuth();
      navigate('/signin');
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setIsSigningOut(false);  // # End loading
    }
  };

  // # Handle modal open
  const openSignOutModal = (e) => {
    if (e) e.stopPropagation();
    setIsSignOutModalOpen(true);
  };

  // # Handle modal close
  const closeSignOutModal = (e) => {
    if (e) e.stopPropagation();
    setIsSignOutModalOpen(false);
  };

  // # Handle confirm sign out
  const handleConfirmSignOut = async (e) => {
    if (e) e.stopPropagation();
    closeSignOutModal();
    await handleSignOut();
  };

  // # Handle search toggle
  const toggleSearch = useCallback((e) => {
    if (e) e.stopPropagation();
    setIsSearchOpen(prev => !prev);
  }, []);

  // # Handle search close
  const handleSearchClose = useCallback(() => {
    setIsSearchOpen(false);
  }, []);

  // # Apply blur effect to main content
  useEffect(() => {
    const mainContent = document.querySelector('.step1_library-main-content, .step2_library-main-content, .lbfrm_chapter-content');
    if (mainContent) {
      mainContent.classList.toggle('libhdr_blur-content', isMenuOpen);
      mainContent.classList.toggle('blur', isMenuOpen);
    }
  }, [isMenuOpen]);

  // # Close menu on escape key and click outside
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsMenuOpen(false);
        setIsQuickLinksOpen(false);
      }
    };

    const handleClickOutside = (event) => {
      const menu = document.querySelector('.libhdr_nav-menu');
      const hamburger = document.querySelector('.libhdr_hamburger');
      const quickLinksButton = document.querySelector('.libhdr_quick-links-button');
      const quickLinksMenu = document.querySelector('.libhdr_quick-links-menu');
      
      if (isMenuOpen && menu && !menu.contains(event.target) && !hamburger.contains(event.target)) {
        setIsMenuOpen(false);
      }

      if (isQuickLinksOpen && quickLinksMenu && !quickLinksMenu.contains(event.target) && !quickLinksButton.contains(event.target)) {
        setIsQuickLinksOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, isQuickLinksOpen]);

  // # Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  return (
    <>
      {isSigningOut && <Loading message="Signing out..." />}
      <header className={`libhdr_library-header ${isDarkMode ? 'libhdr_dark-mode' : ''}`}>
        <div className="libhdr_library-header-content">
          {/* Left: Back Button */}
          <button className="libhdr_lib-back-button" onClick={handleBack}>
            <FaArrowLeft className="libhdr_back-icon" />
            <span className="libhdr_back-text">Back</span>
          </button>

          {/* Theme Toggle Button - Show for all views */}
          <button 
            className="libhdr_theme-toggle-button" 
            onClick={handleThemeToggle}
            aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
          >
            {isDarkMode ? (
              <MdLightMode className="libhdr_theme-icon light" />
            ) : (
              <MdDarkMode className="libhdr_theme-icon dark" />
            )}
          </button>

          {/* Center: Logo */}
          <div 
            className="libhdr_-logo-container"
            onClick={handleLogoClick}
          >
            <img 
              src="/icons/TEMPU_Logo.png" 
              alt="TEMP-U Logo" 
              className="libhdr_lib-header-logo"
            />
          </div>

          {/* Search Button */}
          <button 
            className="libhdr_search-button"
            onClick={toggleSearch}
            aria-label="Search library"
          >
            <FiSearch className="libhdr_search-icon" />
            <span className="libhdr_search-text">Search</span>
          </button>

          {/* Hamburger Menu Button */}
          <button 
            className={`libhdr_hamburger ${isMenuOpen ? 'open' : ''}`}
            onClick={toggleMenu}
            aria-label="Toggle menu"
            aria-expanded={isMenuOpen}
          >
            <span className="libhdr_hamburger-icon"></span>
          </button>

          {/* Menu Overlay */}
          <div 
            className={`libhdr_menu-overlay ${isMenuOpen ? 'open' : ''}`}
            onClick={handleNavigation}
            aria-hidden="true"
          />

          {/* Navigation Menu */}
          <nav 
            className={`libhdr_nav-menu ${isMenuOpen ? 'open' : ''}`}
            aria-label="Main navigation"
          >
            {/* Add Menu Header */}
            <div className={`libhdr_menu-header ${isMenuOpen ? 'open' : ''}`}>
              <button
                className="libhdr_menu-signout-icon"
                onClick={openSignOutModal}
                aria-label="Sign out"
              >
                <FaSignOutAlt size={20} />
              </button>
            </div>

            {/* Sign Out Confirmation Modal */}
            <div className={`libhdr_modal-overlay ${isSignOutModalOpen ? 'open' : ''}`} onClick={closeSignOutModal}>
              <div className="libhdr_modal" onClick={(e) => e.stopPropagation()}>
                <div className="libhdr_modal-header">
                  <FiAlertCircle className="libhdr_modal-icon" />
                  <h3 className="libhdr_modal-title">Sign Out Confirmation</h3>
                </div>
                <div className="libhdr_modal-content">
                  Are you sure you want to sign out? You will need to sign in again to access your account.
                </div>
                <div className="libhdr_modal-actions">
                  <button className="libhdr_modal-button cancel" onClick={closeSignOutModal}>
                    Cancel
                  </button>
                  <button className="libhdr_modal-button confirm" onClick={handleConfirmSignOut}>
                    Sign Out
                  </button>
                </div>
              </div>
            </div>

            <ul>
              <li>
                <Link to={isAdmin ? '/admin' : '/dashboard'} onClick={handleNavigation}>
                  <FiHome className="libhdr_menu-icon" /> Home
                </Link>
              </li>
              
              {/* Quick Links Section */}
              <li className="libhdr_quick-links-section">
                <button 
                  className="libhdr_quick-links-toggle"
                  onClick={toggleQuickLinks}
                  aria-expanded={isQuickLinksOpen}
                >
                  <span>Quick Links (New Tab)</span>
                  <svg 
                    className={`libhdr_quick-links-arrow ${isQuickLinksOpen ? 'open' : ''}`}
                    width="20" 
                    height="20" 
                    viewBox="0 0 24 24"
                  >
                    <path 
                      d="M6 9L12 15L18 9" 
                      stroke="currentColor" 
                      strokeWidth="2" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
                <div className={`libhdr_quick-links-content ${isQuickLinksOpen ? 'open' : ''}`}>
                  {getQuickLinks().map((link, index) => (
                    <Link
                      key={index}
                      to={link.path}
                      className="libhdr_quick-link-item"
                      onClick={handleNavigation}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {link.icon}
                      <span>{link.title}</span>
                    </Link>
                  ))}
                </div>
              </li>

              <li>
                <Link to="/team" onClick={handleNavigation}>
                  <FiUsers className="libhdr_menu-icon" /> Meet Our Team
                </Link>
              </li>
              <li>
                <Link to="/products" onClick={handleNavigation}>
                  <FiPackage className="libhdr_menu-icon" /> Our Products
                </Link>
              </li>
              <li>
                <Link to="/samples" onClick={handleNavigation}>
                  <FiBook className="libhdr_menu-icon" /> Library Samples
                </Link>
              </li>
              <li>
                <Link to={isAdmin ? '/admin/settings' : '/settings'} onClick={handleNavigation}>
                  <FiSettings className="libhdr_menu-icon" /> Settings
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </header>

      {/* Search Component */}
      <LibrarySearch 
        isOpen={isSearchOpen}
        onClose={handleSearchClose}
        isDarkMode={isDarkMode}
      />
    </>
  );
};

export default LibraryHeader;
